import { render } from 'lit';

/**
 * @typedef {Object} ToastConfig
 * @property {'primary' | 'success' | 'neutral' | 'danger' | 'warning'} [variant]
 * @property {number} [duration]
 * @property {'rtl' | 'ltr'} [countdown]
 * @property {boolean} [closable]
 * @property {import('lit').TemplateResult} content
 * @property {() => void} [onClick]
 */

/**
 * Shows a toast alert at a specified screen position.
 *
 * @param {ToastConfig} config
 * @param {'top' | 'bottom'} position - Where on the screen to show the toast
 */
export async function showToast(
  { variant = 'primary', duration = 5_000, countdown = 'rtl', closable = true, content, onClick },
  position = 'top'
) {
  // Set up the alert element with the provided configuration
  const alertElem = document.createElement('sl-alert');
  Object.assign(alertElem, { variant, duration, countdown, closable });
  alertElem.style.display = 'block'; // This is needed for the countdown animation to render correctly

  // Hide the element on click and call the provided callback, if any
  if (onClick) {
    alertElem.addEventListener('click', (event) => {
      const isCloseButton = event.composedPath().some((el) => el instanceof Element && el.part?.contains('close-button'));
      if (isCloseButton) return;
      alertElem.hide();
      onClick();
    });
  }

  // Shoelace only cleans up the alert element from the DOM inside .toast().
  // Since we're calling .show() directly, we handle cleanup ourselves.
  alertElem.addEventListener('sl-after-hide', () => alertElem.remove(), { once: true });

  // Render the provided content template into the alert element
  render(content, alertElem);

  // Set up a toast stack for the specified position if it doesn't already exist
  const stackId = `toast-stack-${position}`;
  let toastStack = document.getElementById(stackId);
  if (!toastStack) {
    toastStack = document.createElement('div');
    toastStack.id = stackId;
    toastStack.className = 'sl-toast-stack'; // inherit Shoelace's toast stack styles
    if (position === 'bottom') {
      Object.assign(toastStack.style, { top: 'auto', bottom: '0', marginBottom: 'var(--sl-spacing-large)' });
    }
    document.body.appendChild(toastStack);
  }

  // Add the alert to the appropriate stack and show it
  toastStack.appendChild(alertElem);
  // Wait for Shoelace's shadow DOM to finish its first render before calling show().
  // Without this, internal elements like `this.base` are null and the countdown animation throws.
  await alertElem.updateComplete;
  alertElem.show();
}
